import React, { useEffect, useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Grid,
  Typography,
  useTheme,
  Autocomplete,
  MenuItem,
  Link,
} from "@mui/material";
import { useFormik } from "formik";
import * as Yup from "yup";
import { useSelector } from "react-redux";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterMoment } from "@mui/x-date-pickers/AdapterMoment";
import { DatePicker } from "@mui/x-date-pickers";
import { InsertDriveFileOutlined } from "@mui/icons-material";
import moment from "moment";
import { BASEURL } from "api/axiosInstance";

const validationSchema = Yup.object({
  kode_jabatan: Yup.string().required("Jabatan wajib diisi"),
  tipe_jenjang: Yup.string().required("Tipe Jenjang wajib diisi"),
  tanggal_berlaku: Yup.mixed().required("Tanggal Berlaku wajib diisi"),
  file_jenjang: Yup.mixed().test(
    "fileType",
    "Hanya mendukung PDF atau gambar",
    (value) => {
      if (!value) return true; // opsional saat edit
      return [
        "application/pdf",
        "image/jpeg",
        "image/png",
      ].includes(value.type);
    },
  ),
});

const toMoment = (val) => (val && moment(val).isValid() ? moment(val) : null);

const EditJenjangModal = ({ open, onClose, dataDetail, onSubmit }) => {
  const theme = useTheme();
  const [newFile, setNewFile] = useState(null);

  const { list_jabatan } = useSelector((state) => state.jabatan);
  const tipeOptions = [
    { value: "Kontrak", text: "Kontrak" },
    { value: "Tetap", text: "Tetap" },
  ];

  useEffect(() => {
    if (!open) setNewFile(null);
  }, [open]);

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: {
      kode_jabatan: dataDetail?.kode_jabatan || "",
      tipe_jenjang: dataDetail?.tipe_jenjang || "",
      tanggal_berlaku: toMoment(dataDetail?.tanggal_berlaku) || moment(),
      tanggal_berakhir: toMoment(dataDetail?.tanggal_berakhir),
    },
    validationSchema,
    onSubmit: (values) => {
      const reqData = {
        kode_jabatan: values.kode_jabatan,
        tipe_jenjang: values.tipe_jenjang,
        tanggal_berlaku: values.tanggal_berlaku
          ? values.tanggal_berlaku.format("YYYY-MM-DD HH:mm:ss")
          : null,
        tanggal_berakhir:
          values.tipe_jenjang === "Tetap"
            ? null
            : values.tanggal_berakhir
              ? values.tanggal_berakhir.format("YYYY-MM-DD HH:mm:ss")
              : null,
        file_jenjang: newFile || null,
      };
      onSubmit(dataDetail.id, reqData);
      formik.resetForm();
      setNewFile(null);
      onClose();
    },
  });

  const handleFileChange = (event) => {
    const file = event.currentTarget.files[0];
    if (file) {
      setNewFile(file);
      formik.setFieldValue("file_jenjang", file);
      formik.setFieldTouched("file_jenjang", true, false);
    }
  };

  const existingFileName = dataDetail?.file_jenjang;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Edit Jenjang Karyawan</DialogTitle>
      <form onSubmit={formik.handleSubmit}>
        <DialogContent dividers>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Karyawan"
                value={`${dataDetail?.id_karyawan ?? "-"} - ${dataDetail?.nama_karyawan ?? "-"}`}
                disabled
              />
            </Grid>
            <Grid item xs={12}>
              <Autocomplete
                fullWidth
                options={list_jabatan}
                getOptionLabel={(option) =>
                  `${option.kode_jabatan} - ${option.nama_jabatan}`
                }
                value={
                  list_jabatan.find(
                    (item) => item.kode_jabatan === formik.values.kode_jabatan,
                  ) || null
                }
                onChange={(event, newValue) => {
                  formik.setFieldValue(
                    "kode_jabatan",
                    newValue?.kode_jabatan || "",
                  );
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Jabatan"
                    name="kode_jabatan"
                    error={
                      formik.touched.kode_jabatan &&
                      Boolean(formik.errors.kode_jabatan)
                    }
                    helperText={
                      formik.touched.kode_jabatan && formik.errors.kode_jabatan
                    }
                  />
                )}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                select
                fullWidth
                label="Tipe Jenjang"
                name="tipe_jenjang"
                value={formik.values.tipe_jenjang}
                onChange={(val) => {
                  formik.setFieldValue("tipe_jenjang", val.target.value);
                  if (val.target.value === "Tetap") {
                    formik.setFieldValue("tanggal_berakhir", null);
                  }
                }}
                error={
                  formik.touched.tipe_jenjang &&
                  Boolean(formik.errors.tipe_jenjang)
                }
                helperText={
                  formik.touched.tipe_jenjang && formik.errors.tipe_jenjang
                }
              >
                {tipeOptions.map(({ text, value }) => (
                  <MenuItem key={value} value={value}>
                    {text}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12} sm={6}>
              <LocalizationProvider dateAdapter={AdapterMoment}>
                <DatePicker
                  label="Tanggal Berlaku"
                  sx={{ width: "100%" }}
                  value={formik.values.tanggal_berlaku}
                  onChange={(val) =>
                    formik.setFieldValue("tanggal_berlaku", val)
                  }
                  format="DD/MM/YYYY"
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      fullWidth
                      error={
                        formik.touched.tanggal_berlaku &&
                        Boolean(formik.errors.tanggal_berlaku)
                      }
                      helperText={
                        formik.touched.tanggal_berlaku &&
                        formik.errors.tanggal_berlaku
                      }
                    />
                  )}
                />
              </LocalizationProvider>
            </Grid>
            <Grid item xs={12} sm={6}>
              <LocalizationProvider dateAdapter={AdapterMoment}>
                <DatePicker
                  label="Tanggal Berakhir"
                  sx={{ width: "100%" }}
                  value={formik.values.tanggal_berakhir}
                  onChange={(val) =>
                    formik.setFieldValue("tanggal_berakhir", val)
                  }
                  disabled={formik.values.tipe_jenjang === "Tetap"}
                  format="DD/MM/YYYY"
                  renderInput={(params) => <TextField {...params} fullWidth />}
                />
              </LocalizationProvider>
            </Grid>

            <Grid item xs={12}>
              {existingFileName && !newFile && (
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ mb: 1, display: "flex", alignItems: "center", gap: 0.5 }}
                >
                  <InsertDriveFileOutlined fontSize="small" />
                  File saat ini:{" "}
                  <Link
                    href={`${BASEURL}/files/${existingFileName}`}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    {existingFileName}
                  </Link>
                </Typography>
              )}
              <Button variant="outlined" component="label" fullWidth>
                {newFile ? `File baru: ${newFile.name}` : "Ganti File Mutasi (PDF)"}
                <input
                  type="file"
                  accept="application/pdf,image/jpeg,image/png"
                  hidden
                  onChange={handleFileChange}
                />
              </Button>
              {newFile && (
                <Button
                  size="small"
                  color="error"
                  onClick={() => {
                    setNewFile(null);
                    formik.setFieldValue("file_jenjang", null);
                  }}
                >
                  Batalkan pilihan file
                </Button>
              )}
              {formik.touched.file_jenjang && formik.errors.file_jenjang && (
                <Typography color="error" variant="body2" mt={1}>
                  {formik.errors.file_jenjang}
                </Typography>
              )}
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button
            variant="contained"
            color="inherit"
            onClick={() => {
              onClose();
              setNewFile(null);
            }}
            sx={{ fontSize: "14px", fontWeight: "bold", padding: "10px 20px" }}
          >
            Batal
          </Button>
          <Button
            type="submit"
            variant="contained"
            sx={{
              backgroundColor: theme.palette.secondary.main,
              color: theme.palette.common.white,
              fontSize: "14px",
              fontWeight: "bold",
              padding: "10px 20px",
              "&:hover": {
                backgroundColor: theme.palette.secondary.dark,
              },
            }}
          >
            Simpan Perubahan
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};

export default EditJenjangModal;
